/**
 * AiTraining.tsx — v0.4.9.4.6
 *
 * The "AI Training" tab inside NetworkView. Reachable by superadmin AND
 * sadmin — routes/lpr.js gates on requireAiTraining, which admits both. Houses all
 * the rule-tuning and model-training surfaces for the AI subsystems.
 *
 * v0.4.9.4.6 starts with 4 subtabs:
 *   - LPR                  — plate OCR rules (the v0.4.9.4.6 deliverable)
 *   - Face Recognition     — placeholder (under construction)
 *   - Wi-Fi CSI sensing    — placeholder (under construction, v0.6)
 *   - Drone Detection      — placeholder (under construction, v0.6+, uses CSI)
 *
 * Tab structure is rendered locally (not via nested routes) because
 * the under-construction subtabs don't have their own routes yet. When
 * each ships, swap the placeholder component for the real one and add
 * a nested route in NetworkView.
 *
 * Mount: in NetworkView, add a tab whose element is `<AiTraining />`. The
 *        component renders its own header + subtab bar.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cpu, Camera, Wifi, Plane } from 'lucide-react';
import LprSetup from './LprSetup';
import FrSetup from './FrSetup';
import WifiCsiSetup from './WifiCsiSetup';
import DroneDetectionSetup from './DroneDetectionSetup';

type Subtab = 'lpr' | 'fr' | 'wifiCsi' | 'droneDetection';

const SUBTABS: { key: Subtab; Icon: React.ComponentType<any>; construction: boolean }[] = [
  { key: 'lpr',            Icon: Cpu,   construction: false },
  { key: 'fr',             Icon: Camera, construction: true  },
  { key: 'wifiCsi',        Icon: Wifi,  construction: true  },
  { key: 'droneDetection', Icon: Plane, construction: true  },
];

export default function AiTraining() {
  const { t } = useTranslation();
  const [active, setActive] = useState<Subtab>('lpr');

  return (
    <div className="p-3 sm:p-6 max-w-6xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          {t('aiTraining.title')}
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          {t('aiTraining.subtitle')}
        </p>
      </div>

      {/* Subtab bar */}
      <div className="flex flex-wrap gap-1 border-b border-gray-300">
        {SUBTABS.map(({ key, Icon, construction }) => {
          const isActive = active === key;
          return (
            <button
              key={key}
              onClick={() => setActive(key)}
              className={
                'flex-1 sm:flex-none min-w-[45%] sm:min-w-0 px-3 sm:px-4 py-2 -mb-px border-b-2 '
              + 'items-center justify-center gap-2 text-xs sm:text-sm transition-colors ' +
                (isActive
                  ? 'border-blue-600 text-gray-900 font-medium'
                  : 'border-transparent text-gray-600 hover:text-gray-900')
              }
              type="button"
            >
              <Icon className="w-4 h-4" />
              {t(`aiTraining.tabs.${key}`)}
              {construction && (
                <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
                  {t('aiTraining.comingSoonBadge')}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Active subtab content */}
      <div className="pt-2">
        {active === 'lpr'            && <LprSetup />}
        {active === 'fr'             && <FrSetup />}
        {active === 'wifiCsi'        && <WifiCsiSetup />}
        {active === 'droneDetection' && <DroneDetectionSetup />}
      </div>
    </div>
  );
}
